import cx from "classnames";
import { type FC } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { ValueError } from "@moment/design-system";

import { useEaselSelectedComputeCellNodeIds } from "~/components/canvas/hooks/useEaselSelectedNodeIds";
import { type CellInfo } from "~/models/cell";

import { ComputedResult, type Selection } from "../ComputedResult";
import { useCachedCell } from "../useCachedCell";
import { useLogCellDiagnostics } from "../useLogCellDiagnostics";
import { useLogCellErrors } from "../useLogCellErrors";

/**
 * ::: Computed Cell :::
 * This component is the react wrapper for a computed cell in prosemirror
 */

export type ComputedCellProps = {
	cellInfo: CellInfo;
	documentId?: string;
	pageId?: string;
};

export const ComputedCell: FC<ComputedCellProps> = ({ cellInfo, documentId, pageId }) => {
	const cachedCell = useCachedCell(cellInfo);
	const selectedComputeCellIDs = useEaselSelectedComputeCellNodeIds();

	useLogCellErrors(cellInfo, cachedCell, documentId, pageId);
	useLogCellDiagnostics(cellInfo, cachedCell, documentId, pageId);

	let selection: Selection = "none";

	if (selectedComputeCellIDs.size === 1 && selectedComputeCellIDs.has(cellInfo.cell.id)) {
		selection = "selected";
	} else if (selectedComputeCellIDs.has(cellInfo.cell.id)) {
		selection = "multiselected";
	}

	const isSelected = selection === "selected" || selection === "multiselected";

	return (
		<div
			className={cx(
				"prosemirror-compute-cell--result",
				{ "prosemirror-compute-cell--selected": isSelected },
				"leading-8", // this is so that the CellControls are aligned with the text
				"-ml-2",
				"max-h-full",
				"min-h-[2rem]",
				{
					"z-canvas-base": isSelected,
					"h-full": false,
					"markdown": cellInfo.cell.code.startsWith("return md`"),
					"select-none": selection === "multiselected",
				}
			)}
			// ref={clickRef}
		>
			<div className={cx("p-0", "whitespace-normal")}>
				<ErrorBoundary
					FallbackComponent={({ error }: { error: Error }) => {
						// if computed result throws an error
						// (like an uncaught promise rejection)
						// then fallback to an error
						return (
							<div className={`cursor-default flex-col`}>
								<ValueError message={error.message} originalError={error} />
							</div>
						);
					}}
				>
					<ComputedResult cellInfo={cellInfo} cachedCell={cachedCell} />
				</ErrorBoundary>
			</div>
		</div>
	);
};
